import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { UnsavedChangesProvider } from "@/lib/unsavedChanges";
import { LogOut, Package, Library, LayoutGrid, Building2, Download, Menu, X, UserCircle2, Truck } from "lucide-react";
import { SubscriptionFooter } from "@/components/SubscriptionFooter";
import { SupportNotificationsProvider } from "@/lib/SupportNotificationsContext";
import { SupportNavLink } from "@/components/SupportNavLink";
import { SubscriptionDisabledBanner } from "@/components/SubscriptionDisabledBanner";
import { ContractUpdateBanner } from "@/components/ContractGate";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

export default function Layout() {
  const { user, logout, isReadonly, subscriptionDisabled } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Auto-close the drawer when the route changes (mobile UX).
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <SupportNotificationsProvider user={user}>
      <div className="flex h-screen overflow-hidden bg-[#FAFAFA]" data-testid="admin-layout">
      {/* Mobile hamburger — visible only below md breakpoint */}
      <button
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 w-10 h-10 flex items-center justify-center bg-[#1A1A1A] text-[#FAF9F6] rounded-sm shadow-lg"
        aria-label="Apri menu"
        data-testid="mobile-menu-open">
        <Menu className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Overlay backdrop (mobile only, when drawer open) */}
      {open && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40"
             onClick={() => setOpen(false)}
             data-testid="mobile-menu-backdrop" />
      )}

      <aside
        className={`sidebar flex flex-col fixed md:sticky md:top-0 left-0 z-50 h-screen transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar">
        <div className="px-6 py-8 border-b border-black/10 flex items-start justify-between">
          <div>
            <div className="font-serif text-2xl text-[#000000]">Cosmely</div>
            <div className="text-[10px] tracking-luxury text-[#525252] mt-1 uppercase">
              {user?.tenant_name || "Cosmetic Manager"}
            </div>
            {isReadonly && (
              <div className="mt-2 inline-block px-2 py-0.5 text-[9px] tracking-[0.2em] uppercase bg-[#FAFAFA] text-[#000000] rounded-sm" data-testid="readonly-badge">
                Sola lettura
              </div>
            )}
          </div>
          <button onClick={() => setOpen(false)}
                  className="md:hidden text-[#525252] hover:text-[#000000]"
                  aria-label="Chiudi menu"
                  data-testid="mobile-menu-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex-1 py-6 overflow-y-auto">
          <NavLink to="/products" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-products">
            <Package className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Prodotti
          </NavLink>
          <NavLink to="/clients" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-clients">
            <Building2 className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Clienti
          </NavLink>
          <NavLink to="/suppliers" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-suppliers">
            <Truck className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Fornitori
          </NavLink>
          <NavLink to="/archives" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-archives">
            <Library className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Archivi
          </NavLink>
          {!subscriptionDisabled && !user?.impersonation && <NavLink to="/downloads" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-downloads">
            <Download className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Download
          </NavLink>}
          <NavLink to="/qr-layout" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-qr-layout">
            <LayoutGrid className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Layout QR
          </NavLink>
          <NavLink to="/vetrina-layout" className={({isActive}) => isActive ? "active" : ""} data-testid="nav-vetrina-layout">
            <LayoutGrid className="inline w-4 h-4 mr-3" strokeWidth={1.5} />Catalogo
          </NavLink>
          {!user?.impersonation && <SupportNavLink />}
        </nav>
        <div className="px-6 py-5 border-t border-black/10 shrink-0">
          <Link to="/profile" data-testid="account-link"
                className="group flex items-center gap-2 mb-3 text-[11px] text-[#525252] hover:text-[#000000] truncate transition-colors">
            <UserCircle2 className="w-4 h-4 shrink-0" strokeWidth={1.5} />
            <span className="truncate underline decoration-dotted underline-offset-4">
              {user?.email}
            </span>
          </Link>
          <button onClick={logout} data-testid="logout-btn"
                  className="text-[11px] tracking-luxury text-[#000000] uppercase hover:text-[#525252] flex items-center">
            <LogOut className="w-3 h-3 mr-2" strokeWidth={1.5} /> Esci
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 h-screen flex flex-col overflow-hidden">
        <ImpersonationBanner />
        <SubscriptionDisabledBanner />
        <ContractUpdateBanner />
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-clip">
          <UnsavedChangesProvider>
            <Outlet />
          </UnsavedChangesProvider>
        </div>
        <SubscriptionFooter />
      </main>
    </div>
    </SupportNotificationsProvider>
  );
}
